import { inject, Injectable, isDevMode } from '@angular/core';
import { AvailableLangs, TranslocoService } from '@jsverse/transloco';
import { LOG_MODULE_KEY } from '../constants/logs.constants';

/**
 * LanguageService provides methods for managing the application's language settings.
 *
 * This service facilitates the retrieval and configuration of default, active,
 * and available languages using the Transloco Language API. It also includes
 * development tools for tracking language changes.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  /**
   * Injecting TranslocoService to manage language operations.
   */
  protected translocoService = inject(TranslocoService);

  /**
   * Default language currently configured in the Transloco service.
   */
  public defaultLang = this.translocoService.getDefaultLang();

  /**
   * Observable that emits whenever the active language changes.
   *
   * If the app is in development mode, this observable logs language changes
   * for debugging purposes.
   */
  public langChange$ = isDevMode() ? this.setLangChange$() : null;

  /**
   * Subscribes to language changes and logs the new language to the console in development mode.
   *
   * **Use Case:** Useful for debugging and tracking language transitions during development.
   *
   * @private
   */
  private setLangChange$(): void {
    this.translocoService.langChanges$.subscribe((lang) => {
      console.log(`${LOG_MODULE_KEY} Language changed to: ${lang}`);
    });
  }

  /**
   * Retrieves the default language configured in the Transloco service.
   *
   * **Use Case:** Allows for dynamic checks or resets to the default language
   * during runtime.
   *
   * @returns The default language code (e.g., 'en', 'es').
   *
   * @see [getDefaultLang()](https://jsverse.gitbook.io/transloco/core-concepts/language-api#getdefaultlang)
   */
  public getDefaultLang(): string {
    return this.translocoService.getDefaultLang();
  }

  /**
   * Updates the default language in the Transloco service.
   *
   * **Use Case:** Adjusts the fallback language for translation lookups.
   *
   * @param defaultLang The language code to set as the default (e.g., 'en', 'es').
   *
   * @see [setDefaultLang()](https://jsverse.gitbook.io/transloco/core-concepts/language-api#setdefaultlang)
   */
  public setDefaultLang(defaultLang: string): void {
    this.translocoService.setDefaultLang(defaultLang);
  }

  /**
   * Sets the active language for the application in the Transloco service.
   *
   * **Use Case:** Dynamically changes the language based on user preferences
   * or other conditions.
   *
   * @param activeLang The language code to activate (e.g., 'en', 'es').
   *
   * @see [setActiveLang()](https://jsverse.gitbook.io/transloco/core-concepts/language-api#setactivelang)
   */
  public setActiveLang(activeLang: string): void {
    this.translocoService.setActiveLang(activeLang);
  }

  /**
   * Configures the available languages in the Transloco service.
   *
   * **Use Case:** Defines the set of languages users can select from within the application.
   *
   * @param langs An object or array representing available languages (e.g., ['en', 'es']).
   *
   * @see [setAvailableLangs()](https://jsverse.gitbook.io/transloco/core-concepts/language-api#setavailablelangs)
   */
  public setAvailableLangs(langs: AvailableLangs): void {
    this.translocoService.setAvailableLangs(langs);
  }

  /**
   * Retrieves the list of available languages configured in the Transloco service.
   *
   * **Use Case:** Useful for populating language selection menus or validating
   * user-selected languages.
   *
   * @returns An object or array representing available languages.
   *
   * @see [getAvailableLangs()](https://jsverse.gitbook.io/transloco/core-concepts/language-api#getavailablelangs)
   */
  public getAvailableLangs(): AvailableLangs {
    return this.translocoService.getAvailableLangs();
  }
}
